import { router } from '@inertiajs/vue3';

/**
 * Click-origin tracking with two buffers:
 *   `pending`   — set on click. Consumed either by the next Inertia visit
 *                 (attached as X-Nav-Source header) OR by a client-side
 *                 consumer on the same page (e.g. useTabTracker for a tab
 *                 switch that didn't navigate).
 *   `delivered` — set when `router.on('before')` attached a pending source
 *                 to a real navigation. Represents "the source that just
 *                 caused a nav" so the destination page's on-mount beacon
 *                 (useTabTracker's immediate fire) can consume it.
 *
 * Both buffers expire after MAX_AGE_MS to keep stale sources from bleeding
 * into unrelated requests.
 *
 * Cross-tab source (target="_blank") is handled *out of band* — the click
 * href includes `?_source=<value>` and the server middleware reads that
 * as a fallback. The URL is stripped on the destination page so the
 * `_source` param doesn't stay visible in the address bar.
 */

let pendingSource = null;
let pendingAt = 0;
let deliveredSource = null;
let deliveredAt = 0;
const MAX_AGE_MS = 1500;

const isFresh = (at) => (Date.now() - at) <= MAX_AGE_MS;

document.addEventListener(
    'click',
    (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const el = target?.closest('[data-nav-source]');
        if (!el) return;

        const source = el.getAttribute('data-nav-source') || null;
        if (!source) return;

        pendingSource = source;
        pendingAt = Date.now();
    },
    true,
);

router.on('before', (event) => {
    if (!pendingSource || !isFresh(pendingAt)) {
        pendingSource = null;
        return;
    }
    const visit = event.detail.visit;
    visit.headers = visit.headers || {};
    visit.headers['X-Nav-Source'] = pendingSource;
    // Promote to `delivered` so the destination page's on-mount beacon
    // (useTabTracker.immediate) can also read the source. Header-based
    // consumption (server middleware) already got its copy above.
    deliveredSource = pendingSource;
    deliveredAt = Date.now();
    pendingSource = null;
});

/**
 * Strip `_source=<value>` from the URL after the server has read it,
 * so it doesn't linger in the address bar or get copy-pasted into
 * bookmarks. Uses replaceState so no navigation is triggered.
 */
const stripSourceParam = () => {
    if (typeof window === 'undefined' || !window.location.search.includes('_source=')) {
        return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('_source');
    const clean = url.pathname + (url.search ? url.search : '') + url.hash;
    window.history.replaceState(window.history.state, '', clean);
};

// Initial page load (target="_blank" opens a new tab → new browser
// context → this runs once) and every subsequent Inertia navigation.
stripSourceParam();
router.on('success', stripSourceParam);

/**
 * Programmatically set the pending source. Use when a click doesn't happen
 * on a `[data-nav-source]` element — e.g., a `<DataTable>` row where the
 * navigation is triggered by the component's own `@row-click` handler,
 * making it awkward to bind data attributes per row.
 *
 * The caller should invoke this synchronously before router.visit (or
 * before whatever triggers the outgoing Inertia visit). The plugin's
 * `router.on('before')` hook consumes it exactly like a click-captured source.
 */
export function setPendingSource(source) {
    if (!source) return;
    pendingSource = source;
    pendingAt = Date.now();
}

export function consumeCurrentSource() {
    // Prefer delivered (post-navigation, for tabbed landing pages).
    if (deliveredSource && isFresh(deliveredAt)) {
        const value = deliveredSource;
        deliveredSource = null;
        return value;
    }
    // Fall back to pending (same-page click, e.g. tab switch on the same route).
    if (pendingSource && isFresh(pendingAt)) {
        const value = pendingSource;
        pendingSource = null;
        return value;
    }
    return null;
}
